import store, { AppDispatch } from '../store';
import { formatDeviceName } from '../util/deviceName';
import endpoint from '../util/endpoint';
import cartService from '@/carts/cart.service';
import { CartLocationMap } from '@/carts/gps/cartsLocations';
import { storeManagerEndpoints } from '@/lib/endpoints';
import type { CustomerLabel } from '@whiz-cart/node-shared/models/customer/customerLabel';
import { SessionActiveDevice } from '@whiz-cart/node-shared/models/session/activeDevice';
import { SessionMonitorSessionDeviceMetadata } from '@whiz-cart/node-shared/models/session/sessionMonitor';
import { CustomerTicket } from '@whiz-cart/node-shared/models/ticket/ticket';
import _ from 'lodash';

export const SET_DEVICES_LOADING = 'SET_DEVICES_LOADING';
export const LOAD_DEVICES_COMPLETE = 'LOAD_DEVICES_COMPLETE';
export const UPDATE_DEVICE = 'UPDATE_DEVICE';
export const LOAD_CARTSLOCATIONS_COMPLETE = 'LOAD_CARTSLOCATIONS_COMPLETE';
export const UPDATE_CART_LOCATION = 'UPDATE_CART_LOCATION';
export const CLOSE_SESSION = 'CLOSE_SESSION';
export const UPDATE_TICKET = 'UPDATE_TICKET';
export const REMOVE_TICKET = 'REMOVE_TICKET';
export const UPDATE_DEVICE_LOCATIONS = 'UPDATE_DEVICE_LOCATIONS';
export const CLEAN_LOCATIONS = 'CLEAN_LOCATIONS';
export const UPDATE_WEIGHTS = 'UPDATE_WEIGHTS';
export const UPDATE_CUSTOMER_LABELS = 'UPDATE_CUSTOMER_LABELS';

const transformDevice = ({
    deviceId,
    targetId,
    deviceMetadata,
    ...device
}:
    | (SessionActiveDevice & { deviceId?: string | null })
    | (CustomerTicket & { targetId?: string; deviceMetadata?: SessionMonitorSessionDeviceMetadata; isMobile?: boolean })) =>
    _.omit(
        {
            ...device,
            deviceId: formatDeviceName(deviceId ?? targetId ?? '', device.isMobile ?? false),
            deviceMetadata: deviceMetadata ?? (device.isMobile ? { hasScale: 'false' } : undefined),
        },
        ['isSessionOpen', 'hasTicket'],
    );

export const setDevicesLoading = (loading: boolean) => ({ type: SET_DEVICES_LOADING, payload: loading });

export const fetchSessions = (storeGuid: string) => async (dispatch: any) => {
    const devices = await storeManagerEndpoints.session.activeDevices({ storeGuid }).fetch();

    dispatch({ type: LOAD_DEVICES_COMPLETE, payload: devices.map(transformDevice) });

    return devices;
};

export const fetchCartsLocations = (storeGuid: string) => async (dispatch: any) => {
    const cartsLocations = await cartService.getLocations.get({ storeGuid });
    const parsedLocations = cartsLocations?.map((location) => {
        return {
            ...location,
            lat: location?.locations?.[0]?.lat ?? 0,
            lon: location?.locations?.[0]?.lon ?? 0,
            lng: location?.locations?.[0]?.lon ?? 0,
            createdOn: location?.locations?.[0]?.createdOn,
        };
    }) as CartLocationMap[];

    dispatch({ type: LOAD_CARTSLOCATIONS_COMPLETE, payload: parsedLocations });

    return parsedLocations;
};

export const updateDevice = (device: SessionActiveDevice) => async (dispatch: any) => {
    dispatch({
        type: UPDATE_DEVICE,
        payload: transformDevice(device),
    });
};

export const updateCartLocation = (cartLocation: CartLocationMap) => ({
    type: UPDATE_CART_LOCATION,
    payload: cartLocation,
});

export const closeSession = (session: { endedOn: string; images: unknown; sessionGuid: string }) => ({
    type: CLOSE_SESSION,
    payload: session,
});

export const updateTicket = (ticket: CustomerTicket) => ({
    type: UPDATE_TICKET,
    payload: transformDevice(ticket),
});

export const removeTicket = (ticketId: string) => ({
    type: REMOVE_TICKET,
    payload: ticketId,
});

export const updateWeights = (msg: any) => ({
    type: UPDATE_WEIGHTS,
    payload: msg,
});

export const updateDeviceLocations = (msg: any) => ({
    type: UPDATE_DEVICE_LOCATIONS,
    payload: msg.map(transformDevice),
});

export const updateCustomerLabels = (customerGuid: string, labels: CustomerLabel[]) => ({
    type: UPDATE_CUSTOMER_LABELS,
    payload: { customerGuid, labels },
});

export const closeTickets = (ticketId: string) => async (dispatch: AppDispatch) => {
    const storeGuid = (await store.awaitState((state) => state.url.params.storeGuid)) as string;
    const response = await endpoint('storeManager.closeTickets', { storeGuid }).post({ ticketId });
    if (!response) {
        throw Error('Failed to close ticket');
    }
    dispatch(removeTicket(ticketId));
};

export async function assignTicket(
    storeGuid: string,
    { userName, deviceId, ticketIds }: { userName?: string; deviceId: string; ticketIds: string[] },
) {
    return await endpoint('storeManager.assignTickets', { storeGuid }).post({ userName, deviceId, ticketIds });
}

export async function unassignTicket(storeGuid: string, { ticketIds }: { ticketIds: string[] }) {
    return await endpoint('storeManager.unassignTickets', { storeGuid }).post({ ticketIds });
}

export const cleanLocations = () => ({ type: CLEAN_LOCATIONS });
